import React, { useState, useEffect, useRef } from 'react';
import { useI18n } from '../../hooks/useI18n';
import { useAuth } from '../../hooks/useAuth';
import { verifySms, requestSms } from '../../api/authApi';
import { COUNTRIES, getCountryName } from './countries';

interface OtpFormProps {
  phone: string;
  onChangeNumber: () => void;
  context?: 'login' | 'register';
}

export const OtpForm: React.FC<OtpFormProps> = ({ phone, onChangeNumber, context = 'login' }) => {
  const { t, lang } = useI18n();
  const { login } = useAuth();
  
  const [code, setCode] = useState(['', '', '', '']);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [timer, setTimer] = useState(60);
  
  const inputRefs = [
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
  ];

  useEffect(() => {
    if (timer > 0) {
      const interval = setInterval(() => setTimer((t) => t - 1), 1000);
      return () => clearInterval(interval);
    }
  }, [timer]);

  useEffect(() => {
    if (inputRefs[0].current) {
      inputRefs[0].current.focus();
    }

    // WebOTP API for Android / PWA
    if ('OTPCredential' in window) {
      const abortController = new AbortController();
      
      navigator.credentials.get({
        otp: { transport: ['sms'] },
        signal: abortController.signal
      } as any).then((otp: any) => {
        if (otp && otp.code && typeof otp.code === 'string') {
          const pasted = otp.code.slice(0, 4).split('');
          const newCode = ['', '', '', ''];
          for (let i = 0; i < 4; i++) {
            if (pasted[i]) newCode[i] = pasted[i];
          }
          setCode(newCode);
        }
      }).catch(err => {
        console.log('WebOTP API Error:', err);
      });

      return () => abortController.abort();
    }
  }, []);

  const handleChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;

    const newCode = [...code];
    if (value.length > 1) {
      const pasted = value.slice(0, 4).split('');
      for (let i = 0; i < 4; i++) {
        if (pasted[i]) newCode[i] = pasted[i];
      }
      setCode(newCode);
      const nextEmpty = newCode.findIndex((c) => !c);
      if (nextEmpty !== -1) {
        inputRefs[nextEmpty].current?.focus();
      } else {
        inputRefs[3].current?.focus();
      }
      return;
    }

    newCode[index] = value;
    setCode(newCode);

    if (value && index < 3) {
      inputRefs[index + 1].current?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs[index - 1].current?.focus();
    }
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (isLoading) return;
    const fullCode = code.join('');
    if (fullCode.length !== 4) return;

    setIsLoading(true);
    setError('');

    try {
      const data = await verifySms(phone, fullCode, context);
      login(data.token, data.refreshToken);
    } catch (err: any) {
      setError(err.response?.data?.message || t('err_otp_invalid'));
      setCode(['', '', '', '']);
      inputRefs[0].current?.focus();
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (code.every((c) => c !== '')) {
      handleSubmit();
    }
  }, [code]);

  const handleResend = async () => {
    if (timer > 0 || isLoading) return;
    setIsLoading(true);
    setError('');
    try {
      await requestSms(phone, context);
      setTimer(60);
      setCode(['', '', '', '']);
      inputRefs[0].current?.focus();
    } catch (err: any) {
      setError(err.response?.data?.message || t('err_sms_failed'));
    } finally {
      setIsLoading(false);
    }
  };

  let displayPhone = phone.startsWith('+') ? phone : `+${phone.replace(/\D/g, '')}`;
  const matchedCountry = COUNTRIES.find(c => displayPhone.startsWith(c.dialCode));
  if (matchedCountry) {
    if (matchedCountry.dialCode === '+48') {
      const rest = displayPhone.substring(3);
      if (rest.length === 9) {
        displayPhone = `+48 ${rest.slice(0, 3)} ${rest.slice(3, 6)} ${rest.slice(6)}`;
      }
    } else {
      displayPhone = `${matchedCountry.dialCode} ${displayPhone.substring(matchedCountry.dialCode.length)}`;
    }
  }

  return (
    <div className="cab-auth-form cab-otp-form">
      <div className="cab-auth-header-wrapper">
        <h2>{t('otp_title')}</h2>
        <p className="cab-sub">
          {t('otp_sent_hint', { phone: displayPhone })}
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="cab-otp-inputs">
          {code.map((digit, i) => (
            <input
              key={i}
              ref={inputRefs[i]}
              type="text"
              inputMode="numeric"
              autoComplete={i === 0 ? "one-time-code" : "off"}
              maxLength={4}
              className={`cab-otp-inp ${error ? 'error' : ''}`}
              value={digit}
              onChange={(e) => handleChange(i, e.target.value)}
              onKeyDown={(e) => handleKeyDown(i, e)}
              disabled={isLoading}
            />
          ))}
        </div>

        {error && <div className="cab-error-msg">{error}</div>}

        <button 
          type="submit" 
          className="cab-btn-primary" 
          disabled={isLoading}
        >
          {isLoading ? '...' : t('confirm')}
        </button>
      </form>

      <div className="cab-otp-actions">
        <button 
          type="button"
          className={`cab-resend-btn ${timer > 0 ? 'disabled' : ''}`}
          onClick={handleResend}
          disabled={timer > 0 || isLoading}
        >
          {timer > 0 
            ? t('resend_timer', { time: `0:${timer.toString().padStart(2, '0')}` }) 
            : t('resend')}
        </button>

        <button 
          className="cab-change-number"
          onClick={onChangeNumber}
          disabled={isLoading}
          type="button"
        >
          {t('change_number')}
        </button>
      </div>

      <style>{`
        .cab-auth-form * {
          box-sizing: border-box;
        }
        .cab-auth-form {
          background: #F8F6FC;
          border-radius: 32px;
          padding: 48px 32px;
          width: 100%;
          max-width: 644px;
          margin: 0 auto;
          text-align: center;
          font-family: inherit;
          margin-top: 50px;
        }
        .cab-auth-header-wrapper {
          margin-bottom: 24px;
        }
        .cab-auth-form h2 {
          font-size: 26px;
          font-weight: 800;
          color: #361777;
          margin-bottom: 12px;
        }
        .cab-sub {
          font-size: 16px;
          color: #4C3C73;
          line-height: 1.4;
          font-weight: 500;
          margin: 0 auto;
          max-width: 320px;
        }
        .cab-otp-inputs {
          display: flex;
          justify-content: center;
          gap: 16px;
          margin-bottom: 24px;
        }
        .cab-otp-inp {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          border: 1px solid #E4DFED;
          background: #FFFFFF;
          font-size: 24px;
          font-weight: 600;
          text-align: center;
          color: #361777;
          font-family: inherit;
          transition: border-color 0.2s, box-shadow 0.2s;
          box-shadow: 0 2px 8px rgba(45, 21, 97, 0.02);
        }
        .cab-otp-inp:focus {
          outline: none;
          border-color: #361777;
          box-shadow: 0 0 0 1px #361777;
        }
        .cab-otp-inp.error {
          border-color: #FF5C5C;
          background: #FFFFFF;
        }
        .cab-error-msg {
          color: #FF5C5C;
          font-size: 15px;
          margin-bottom: 24px;
          font-weight: 500;
          text-align: center;
        }
        .cab-btn-primary {
          width: 100%;
          height: 56px;
          border-radius: 100px;
          background: #361777;
          color: #fff;
          font-size: 16px;
          font-weight: 600;
          border: none;
          cursor: pointer;
          transition: opacity 0.2s;
          margin-bottom: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-family: inherit;
        }
        .cab-btn-primary:hover {
          opacity: 0.9;
        }
        .cab-btn-primary:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .cab-otp-actions {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 16px;
          margin-top: 24px;
        }
        .cab-resend-btn {
          background: none;
          border: none;
          color: #4C3C73;
          font-weight: 700;
          font-size: 16px;
          cursor: pointer;
          font-family: inherit;
        }
        .cab-resend-btn.disabled {
          cursor: default;
        }
        .cab-change-number {
          background: none;
          border: none;
          color: #A49BB6;
          font-size: 15px;
          font-weight: 500;
          cursor: pointer;
          text-decoration: underline;
          text-decoration-color: #D3CCE3;
          text-underline-offset: 4px;
          font-family: inherit;
        }
        
        @media (max-width: 480px) {
          .cab-auth-form {
            padding: 32px 20px;
            border-radius: 24px;
          }
          .cab-otp-inputs {
            gap: 12px;
          }
          .cab-otp-inp {
            width: 48px;
            height: 48px;
            font-size: 20px;
          }
        }
      `}</style>
    </div>
  );
};
